import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Trash2,
  RotateCcw,
  FileText,
  Folder,
  Loader2,
  AlertTriangle,
  User,
  Clock,
} from "lucide-react";
import { cn } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function HistoryPage() {
  const [trashItems, setTrashItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [emptyConfirm, setEmptyConfirm] = useState(false);

  const fetchTrash = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/trash`);
      setTrashItems(response.data);
    } catch (error) {
      console.error("Failed to fetch trash:", error);
      toast.error("Failed to load recycle bin");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrash();
  }, [fetchTrash]);

  const handleRestore = async (item) => {
    try {
      await axios.post(`${API_URL}/trash/restore/${encodeURIComponent(item.trash_path)}`);
      fetchTrash();
      toast.success("Item restored");
    } catch (error) {
      toast.error("Failed to restore item");
    }
  };

  const handleDelete = async (item) => {
    try {
      await axios.delete(`${API_URL}/trash/${encodeURIComponent(item.trash_path)}`);
      setDeleteConfirm(null);
      fetchTrash();
      toast.success("Item permanently deleted");
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleEmptyTrash = async () => {
    try {
      await axios.delete(`${API_URL}/trash`);
      setEmptyConfirm(false);
      fetchTrash();
      toast.success("Recycle bin emptied");
    } catch (error) {
      toast.error("Failed to empty recycle bin");
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="history-page">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trash2 className="w-5 h-5 text-primary" />
          <h1 className="text-xl font-semibold">Recycle Bin</h1>
          <span className="text-sm text-muted-foreground">
            ({trashItems.length} items)
          </span>
        </div>
        {trashItems.length > 0 && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setEmptyConfirm(true)}
            data-testid="empty-trash-button"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Empty Recycle Bin
          </Button>
        )}
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : trashItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Trash2 className="w-12 h-12 text-muted-foreground mb-4" />
              <h2 className="text-lg font-medium mb-2">Recycle Bin is Empty</h2>
              <p className="text-sm text-muted-foreground">
                Deleted notes and folders will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {trashItems.map((item, index) => {
                const itemName = item.trash_path.split("_").slice(2).join("_") || item.original_path;
                
                return (
                  <div
                    key={item.trash_path}
                    className="flex items-start gap-4 p-4 rounded-lg border bg-card group hover:border-primary/20 transition-colors"
                    data-testid={`trash-item-${index}`}
                  >
                    {item.type === "folder" ? (
                      <Folder className="w-5 h-5 text-primary/70 mt-0.5 flex-shrink-0" />
                    ) : (
                      <FileText className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{itemName}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Deleted {new Date(item.deleted_at).toLocaleString()}
                        </span>
                        {item.deleted_by && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            by {item.deleted_by}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Original: {item.original_path}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestore(item)}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Restore
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteConfirm(item)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this item. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm)}>
              Delete Forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Empty Trash Confirmation */}
      <AlertDialog open={emptyConfirm} onOpenChange={setEmptyConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Empty Recycle Bin
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {trashItems.length} items in your recycle bin. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEmptyTrash} className="bg-destructive hover:bg-destructive/90">
              Empty Recycle Bin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
